import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  useOAuthLinkService,
  OAuthProviderType,
  EmailMismatchError,
  OAuthAccountAlreadyLinkedError,
  ProviderAlreadyLinkedError,
  InvalidOAuthProviderError
} from 'nekst-api';
import { useEventsPublisher } from 'event-bus';
import { OAUTH_LINKS_UPDATED } from 'nekst-api';
import useSettingsNavigator from '../../settingsNavigator';

export type SettingsOAuthCallbackStatus = 'processing' | 'success' | 'error';

export interface SettingsOAuthCallbackState {
  status: SettingsOAuthCallbackStatus;
  errorMessage: string;
  provider: OAuthProviderType | null;
}

export function useSettingsOAuthCallback() {
  const [searchParams] = useSearchParams();
  const { provider: providerParam } = useParams<{ provider: string }>();
  const navigate = useNavigate();
  const settingsNavigator = useSettingsNavigator();

  // Use refs to prevent unnecessary re-renders and keep services stable
  const oAuthLinkServiceRef = useRef(useOAuthLinkService());
  const eventsPublisherRef = useRef(useEventsPublisher());

  // Track processed codes to prevent duplicate processing
  const processedCodesRef = useRef(new Set<string>());

  const [state, setState] = useState<SettingsOAuthCallbackState>({
    status: 'processing',
    errorMessage: '',
    provider: null
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Convert provider parameter to enum
  const getProviderType = (provider: string): OAuthProviderType | null => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return OAuthProviderType.GOOGLE;
      case 'apple':
        return OAuthProviderType.APPLE;
      default:
        return null;
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof EmailMismatchError) {
      const emailError = error as EmailMismatchError;
      return `The ${emailError.providedEmail} email from your OAuth account doesn't match your current account email (${emailError.expectedEmail}). Please use the correct account.`;
    }

    if (error instanceof OAuthAccountAlreadyLinkedError) {
      return 'This OAuth account is already linked to another user. Please use a different account.';
    }

    if (error instanceof ProviderAlreadyLinkedError) {
      return 'You already have this provider connected. Disconnect it first before connecting a new one.';
    }

    if (error instanceof InvalidOAuthProviderError) {
      return 'Invalid OAuth provider. Please try again.';
    }

    return 'Failed to connect your account. Please try again.';
  };

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        // Prevent concurrent processing
        if (isProcessing) {
          return;
        }

        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const provider = getProviderType(providerParam || '');

        if (!provider) {
          setState({
            status: 'error',
            errorMessage: 'Invalid OAuth provider specified.',
            provider: null
          });
          return;
        }

        setState(prev => ({ ...prev, provider }));

        if (error) {
          setState(prev => ({
            ...prev,
            status: 'error',
            errorMessage: 'OAuth authorization was cancelled or failed.'
          }));
          return;
        }

        if (!code) {
          setState(prev => ({
            ...prev,
            status: 'error',
            errorMessage: 'No authorization code received from OAuth provider.'
          }));
          return;
        }

        // Check if we've already processed this code
        if (processedCodesRef.current.has(code)) {
          return;
        }

        // Mark this code as being processed
        processedCodesRef.current.add(code);
        setIsProcessing(true);

        // Process the OAuth account linking
        await oAuthLinkServiceRef.current.linkExistingAccount(provider, { code });

        setState(prev => ({
          ...prev,
          status: 'success',
          errorMessage: ''
        }));

        // Publish OAuth links updated event to refresh the widget
        setTimeout(() => {
          eventsPublisherRef.current.publish(OAUTH_LINKS_UPDATED, {});
        }, 500);

        // Redirect to settings password page after a brief success message
        setTimeout(() => {
          navigate(settingsNavigator.getUpdatePasswordPageLink());
        }, 1500);

      } catch (error) {
        console.error('Settings OAuth callback error:', error);

        setState(prev => ({
          ...prev,
          status: 'error',
          errorMessage: getErrorMessage(error)
        }));
      } finally {
        setIsProcessing(false);
      }
    };

    processOAuthCallback();
  }, []);

  const handleRetry = () => {
    settingsNavigator.openUpdatePasswordPage();
  };

  const handleReturnToSettings = () => {
    settingsNavigator.openUpdatePasswordPage();
  };

  return {
    ...state,
    handleRetry,
    handleReturnToSettings
  };
}
